/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, msdyn365Commerce } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { IPriceComponentProps } from '@msdyn365-commerce/components';
import * as React from 'react';

const defaultPrice: number = 0;
const freePrice: number = 0;

export interface IPriceComponent extends IComponent<IPriceComponentProps> {}

const PriceComponentActions = {};

const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps) => {
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: IPriceComponentProps): boolean => {
    const originalPrice = getOriginalPrice(props);
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return props.data.price.MinVariantPrice < originalPrice;
    }


    if (originalPrice && Number(props.data.price.CustomerContextualPrice?.toFixed(2))) {
        return originalPrice > Number(props.data.price.CustomerContextualPrice?.toFixed(2));
    }

    return false;
};

const getOriginalPrice = (props: IPriceComponentProps): number => {
    const price = Math.max(
        props.data.price.BasePrice ?? defaultPrice,
        props.data.price.TradeAgreementPrice ?? defaultPrice,
        props.data.price.AdjustedPrice ?? defaultPrice
    );
    return parseFloat(price.toFixed(2));
};

const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false);
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                <span className='msc-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }

    return (
        <span className='msc-price__actual' itemProp='price'>
            {formatCurrency(props, props.data.price.CustomerContextualPrice, true)}
        </span>
    );
};

const formatCurrency = (props: IPriceComponentProps, price: number | undefined, shouldUseFreePriceText: boolean | undefined): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price);
};

const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element | null => {
    const originalPrice: number = getOriginalPrice(props);
    const initialPrice: string = formatCurrency(props, originalPrice, true);
    const initialPriceNumber = Number(props.data.price.CustomerContextualPrice?.toFixed(2));
    const currentPriceNumber = Math.max(
        props.data.price.BasePrice || 0,
        props.data.price.TradeAgreementPrice || 0,
        props.data.price.AdjustedPrice || 0
    );

    const discountedPrice = formatCurrency(props, initialPriceNumber, true);

    let discountedPricePercentage;
    let difference = currentPriceNumber - initialPriceNumber!;
    let discount = difference / currentPriceNumber;
    discountedPricePercentage = discount * 100;

    return (
        <>
            <span className='msc-price__strikethrough' aria-hidden='true'>
                {initialPrice}
            </span>
            <span className='discount-tag'>-{discountedPricePercentage.toFixed(0)}%</span>
            <span className='msc-price__actual' itemProp='price' style={{ color: '#EC008C', fontFamily: "'Conv_EuropaBold',sans-serif" }}>
                {discountedPrice}
            </span>
            {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
        </>
    );
};

export const CustomPriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<
    IPriceComponent
>('Price', { component: Price, ...PriceComponentActions });

export default CustomPriceComponent;
